import { Link } from "react-router-dom"
import { Container } from "@/components/layout/Container"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface SplitFeatureCta {
	label: string
	to: string
}

interface SplitFeatureProps {
	eyebrow?: string
	title?: React.ReactNode
	body?: string
	/** Imagen lateral. Si se omite, se reserva el espacio vacío igualmente. */
	image?: string
	imageAlt?: string
	/**
	 * Imagen de fondo, limitada al ancho del Container (panel redondeado). Puede ser
	 * una sola ruta o un par responsivo `{ sm, lg }` (móvil vs. pantallas grandes).
	 */
	backgroundImage?: string | { sm: string; lg: string }
	/** Coloca el bloque visual a la derecha (por defecto a la izquierda en desktop). */
	reverse?: boolean
	cta?: SplitFeatureCta
	/** Texto claro (blanco), para usar sobre `backgroundImage` con mucho color. */
	light?: boolean
	/**
	 * Añade padding superior simétrico al inferior. Por defecto la sección solo
	 * lleva padding inferior (asume una sección previa que aporta el aire de arriba,
	 * como en el home tras `FeatureGrid`). Actívalo cuando la sección anterior no
	 * deja espacio arriba (p. ej. en la ficha de producto).
	 */
	padTop?: boolean
}

/**
 * Bloque de imagen + texto, apilado en móvil y a dos columnas desde `md`.
 * - `image`: foto lateral; si se omite, la columna queda como espacio reservado.
 * - `backgroundImage`: imagen de fondo en panel limitado al Container; admite par
 *   responsivo `{ sm, lg }`. Combínalo con `light` para texto en blanco.
 *
 * Reutilizable para "Para toda la familia", "Sobre nosotros", CTAs, etc.
 */
export function SplitFeature({
	eyebrow,
	title,
	body,
	image,
	imageAlt = "",
	backgroundImage,
	reverse = false,
	cta,
	light = false,
	padTop = false
}: SplitFeatureProps) {
	return (
		<section className="relative overflow-hidden bg-background">
			<Container className={cn("pb-20 md:pb-28", padTop && "pt-20 md:pt-28")}>
				{/* Con imagen de fondo, el contenido vive en un panel que respeta el padding
				    del Container: así tiene márgenes laterales en móvil y el padding vertical
				    del Container actúa como margen arriba/abajo del panel. */}
				<div
					className={cn(
						"relative grid items-center gap-10 md:grid-cols-2 md:gap-12 lg:gap-16",
						backgroundImage && "overflow-hidden rounded-3xl p-8 sm:p-10 md:p-12 lg:p-14"
					)}
				>
					{/* Imagen de fondo del panel. Si es responsiva, se monta una por
					    breakpoint; el navegador no descarga la oculta (`display:none`). */}
					{backgroundImage &&
						(typeof backgroundImage === "string" ? (
							<div
								aria-hidden
								className="absolute inset-0 bg-cover bg-center"
								style={{ backgroundImage: `url(${backgroundImage})` }}
							/>
						) : (
							<>
								<div
									aria-hidden
									className="absolute inset-0 bg-cover bg-center md:hidden"
									style={{ backgroundImage: `url(${backgroundImage.sm})` }}
								/>
								<div
									aria-hidden
									className="absolute inset-0 hidden bg-cover bg-center md:block"
									style={{ backgroundImage: `url(${backgroundImage.lg})` }}
								/>
							</>
						))}

					{/* Columna visual: imagen si se provee; si no, espacio reservado. */}
					<div className={cn("relative", reverse && "md:order-last")}>
						{image ? (
							<>
								<div
									aria-hidden
									className="absolute -inset-3 rounded-[2rem] bg-linear-to-tr from-primary/15 to-chart-2/15 blur-xl"
								/>
								<img
									src={image}
									alt={imageAlt}
									className="relative aspect-4/3 w-full rounded-[2rem] object-cover shadow-xl ring-1 ring-foreground/10"
									loading="lazy"
								/>
							</>
						) : (
							<div aria-hidden className="aspect-4/3 w-full" />
						)}
					</div>

					<div className={cn("relative text-center md:text-right", light && "text-white")}>
						{eyebrow && (
							<span
								className={cn(
									"inline-block rounded-full px-3 py-1 text-sm font-medium",
									light ? "bg-white/15 text-white" : "bg-primary/10 text-primary"
								)}
							>
								{eyebrow}
							</span>
						)}
						<h2 className="mt-4 text-balance text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">
							{title}
						</h2>
						<p
							className={cn(
								"mx-auto mt-6 max-w-xl text-pretty text-lg leading-relaxed md:mx-0",
								light ? "text-white/85" : "text-muted-foreground"
							)}
						>
							{body}
						</p>
						{cta && (
							<div className="mt-8 flex justify-center md:justify-start">
								<Button asChild size="lg">
									<Link to={cta.to}>{cta.label}</Link>
								</Button>
							</div>
						)}
					</div>
				</div>
			</Container>
		</section>
	)
}
